import { memo, type JSX, type ReactNode } from "react";
import { Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import { AddRegular, ArrowClockwiseRegular, ArrowCounterclockwiseRegular, ArrowLeftRegular, ArrowRightRegular, CopyRegular, DeleteRegular, DeveloperBoardRegular, DismissRegular, DocumentArrowDownRegular, DocumentArrowUpRegular, DocumentTextRegular, EditRegular, FolderRegular, HistoryRegular, MoreHorizontalRegular, PanelBottomRegular, PanelLeftRegular, PersonRegular, MaximizeRegular, PlayRegular, SaveRegular, SearchRegular, SettingsRegular, SparkleRegular, StopRegular, SubtractRegular } from "@fluentui/react-icons";
import type { OptimizationStudy, SolverHistoryEntry } from "@minglun/domain";
import type { SolverProgressV2 } from "@minglun/protocol";
import { SolverRunDetails } from "../solver-run-progress";
import { type Page, isStudyPage, developerPages, formatNumber, formatElapsedSeconds, solverResultStatus, useSolverElapsed } from "../workbench-shared";

export function WindowControls({
  onClose,
}: { onClose?: () => void } = {}): JSX.Element {
  return (
    <div className="window-controls" aria-label="窗口控制">
      <button
        type="button"
        className="window-control"
        aria-label="最小化"
        title="最小化"
        onClick={() => window.minglun.minimizeWindow()}
      >
        <SubtractRegular />
      </button>
      <button
        type="button"
        className="window-control"
        aria-label="最大化或还原"
        title="最大化或还原"
        onClick={() => window.minglun.toggleMaximizeWindow()}
      >
        <MaximizeRegular />
      </button>
      <button
        type="button"
        className="window-control close"
        aria-label="关闭"
        title="关闭"
        onClick={onClose ?? (() => window.minglun.closeWindow())}
      >
        <DismissRegular />
      </button>
    </div>
  );
}

export function AppMenuBar(props: {
  canUndo: boolean;
  canRedo: boolean;
  onUndo(): void;
  onRedo(): void;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack(): void;
  onForward(): void;
  developerMode: boolean;
  developerAllowed: boolean;
  onToggleDeveloper(): void;
  saving: boolean;
  dirty: boolean;
  busy: boolean;
  onSave(): void;
  onClose(): void;
  onNewStudy(): void;
  onRun(): void;
  onCancelRun(): void;
  running: boolean;
  solverAllowed: boolean;
  sidebarVisible: boolean;
  bottomPanelOpen: boolean;
  onToggleSidebar(): void;
  onToggleBottomPanel(): void;
  onNavigate(page: Page): void;
  onSearch(): void;
}): JSX.Element {
  return (
    <header className="app-menubar">
      <div className="app-brand">
        <span className="brand-mark">
          <SparkleRegular />
        </span>
        <strong>命轮工坊</strong>
      </div>
      <nav className="ide-main-menu" aria-label="应用菜单"><Menu><MenuTrigger disableButtonEnhancement><button type="button" className="ide-menu-trigger">更多</button></MenuTrigger><MenuPopover><MenuList>
        <MenuItem icon={<AddRegular />} onClick={props.onNewStudy}>新建方案</MenuItem>
        <MenuItem icon={<PanelLeftRegular />} onClick={props.onToggleSidebar}>{props.sidebarVisible?'收起方案列表':'展开方案列表'}</MenuItem>
        <MenuItem icon={<HistoryRegular />} onClick={()=>props.onNavigate('results')}>结果记录</MenuItem>
        <MenuDivider />
        <MenuItem icon={<SearchRegular />} onClick={props.onSearch}>查找 <span className="menu-shortcut">Ctrl+F</span></MenuItem>
        <MenuItem icon={<StopRegular />} disabled={!props.running} onClick={props.onCancelRun}>停止计算</MenuItem>
        <MenuItem icon={<PanelBottomRegular />} onClick={props.onToggleBottomPanel}>运行面板</MenuItem>
        <MenuDivider />
        <MenuItem icon={<SettingsRegular />} disabled={!props.developerAllowed} onClick={()=>props.onNavigate('global-settings')}>设置</MenuItem>
        <MenuItem icon={<DeveloperBoardRegular />} disabled={!props.developerAllowed} onClick={props.onToggleDeveloper}>{props.developerMode?'关闭开发者模式':'开发资料'}</MenuItem>
        <MenuItem icon={<DocumentTextRegular />} disabled={!props.developerMode} onClick={()=>props.onNavigate('logs')}>运行日志</MenuItem>
        <MenuDivider />
        <MenuItem icon={<DismissRegular />} onClick={props.onClose}>退出</MenuItem>
      </MenuList></MenuPopover></Menu></nav>
      <button type="button" className="command-search" title="查找当前编辑器（Ctrl+F）" onClick={props.onSearch}><SearchRegular /><span>搜索</span><kbd>Ctrl F</kbd></button>
      <span className="appbar-drag" />
      <WindowControls onClose={props.onClose} />
    </header>
  );
}

export function IdeToolbar(props: {
  page: Page; studyName: string; pageLabel: string; canGoBack: boolean; canGoForward: boolean; onBack(): void; onForward(): void;
  canUndo: boolean; canRedo: boolean; onUndo(): void; onRedo(): void; saving: boolean; dirty: boolean; onSave(): void;
  onNewStudy(): void; onOpenStudy(): void; onOpenDeveloper(): void;
  onRun(): void; running: boolean; solverAllowed: boolean; onShowRun(): void;
  saveState: ReactNode; onToggleSidebar():void; onImport():void; onExport():void; onRename():void; onDuplicate():void; onClose():void;
}): JSX.Element {
  return <div className="ide-toolbar" role="toolbar" aria-label="常用操作">
    <button type="button" className="ide-tool" aria-label="展开或收起列表" onClick={props.onToggleSidebar}><PanelLeftRegular /></button>
    <strong className="workspace-title" title={props.studyName}>{isStudyPage(props.page) ? props.studyName : props.page === 'personal' ? '我的' : props.pageLabel}</strong>
    <div className="toolbar-save-state">{props.saveState}</div>
    <div className="ide-toolbar-group">
      <button type="button" className="ide-tool" disabled={!props.canUndo} onClick={props.onUndo} aria-label="撤销" title="撤销（Ctrl+Z）"><ArrowCounterclockwiseRegular /></button>
      <button type="button" className="ide-tool" disabled={!props.canRedo} onClick={props.onRedo} aria-label="重做" title="重做（Ctrl+Y）"><ArrowClockwiseRegular /></button>
    </div>
    <Menu><MenuTrigger disableButtonEnhancement><button type="button" className="ide-tool" aria-label="更多操作" title="更多操作"><MoreHorizontalRegular /></button></MenuTrigger><MenuPopover><MenuList>
      <MenuItem disabled={!props.canUndo} onClick={props.onUndo}>撤销</MenuItem>
      <MenuItem disabled={!props.canRedo} onClick={props.onRedo}>重做</MenuItem>
      <MenuDivider/>
      <MenuItem icon={<AddRegular/>} onClick={props.onNewStudy}>新建方案</MenuItem>
      <MenuItem icon={<CopyRegular/>} onClick={props.onDuplicate}>复制此方案</MenuItem>
      <MenuItem icon={<EditRegular/>} onClick={props.onRename}>重命名与说明</MenuItem>
      <MenuDivider/>
      <MenuItem icon={<DocumentArrowDownRegular/>} onClick={props.onImport}>导入方案</MenuItem>
      <MenuItem icon={<DocumentArrowUpRegular/>} onClick={props.onExport}>导出当前方案</MenuItem>
      <MenuItem icon={<SaveRegular/>} disabled={props.saving || !props.dirty} onClick={props.onSave}>保存（Ctrl+S）</MenuItem>
      <MenuItem icon={<PanelBottomRegular/>} onClick={props.onShowRun}>运行面板</MenuItem>
      <MenuDivider/>
      <MenuItem disabled={!props.canGoBack} onClick={props.onBack}>后退</MenuItem>
      <MenuItem disabled={!props.canGoForward} onClick={props.onForward}>前进</MenuItem>
      <MenuItem onClick={props.onClose}>退出</MenuItem>
    </MenuList></MenuPopover></Menu>
    {isStudyPage(props.page) && <button type="button" className="context-run-button" disabled={!props.running && !props.solverAllowed} onClick={props.running ? props.onShowRun : props.onRun} title={props.running ? '查看运行进度' : '开始计算（Ctrl+Enter）'}><PlayRegular /><span>{props.running ? '查看进度' : '开始计算'}</span></button>}
    <WindowControls onClose={props.onClose}/>
  </div>;
}

export function ActivityRail(props: {
  page: Page; developerMode: boolean; developerAllowed: boolean; onStudies(): void; onResults(): void; onDeveloper(): void; onSettings(): void; onPersonal(): void;
}): JSX.Element {
  const inStudy = isStudyPage(props.page);
  return <nav className="unified-navigation" aria-label="主导航">
    <button type="button" aria-current={inStudy ? 'page' : undefined} onClick={props.onStudies} title="方案"><FolderRegular /><span>方案</span></button>
    <button type="button" aria-current={props.page === 'results' ? 'page' : undefined} onClick={props.onResults} title="结果"><HistoryRegular /><span>结果</span></button>
    <button type="button" aria-current={!inStudy && props.page !== 'results' ? 'page' : undefined} onClick={props.onPersonal} title="我的"><PersonRegular /><span>我的</span></button>
  </nav>;
}

export function StudyExplorer(props: {
  studies: OptimizationStudy[]; activeStudyId: string | undefined; histories: SolverHistoryEntry[];
  onNew(): void; onSelectStudy(id: string): void;
  onDuplicate(id: string): void; onRename(study: OptimizationStudy): void; onDelete(study: OptimizationStudy): void;
  onImport(): void; onExportCurrent(): void; onExportAll(): void;
}): JSX.Element {
  return <aside className="study-explorer">
    <header><strong>方案</strong><span className="explorer-head-actions"><Menu><MenuTrigger disableButtonEnhancement><button type="button" aria-label="方案导入导出" title="方案导入导出"><MoreHorizontalRegular /></button></MenuTrigger><MenuPopover><MenuList><MenuItem icon={<DocumentArrowDownRegular />} onClick={props.onImport}>导入方案…</MenuItem><MenuDivider /><MenuItem icon={<DocumentArrowUpRegular />} onClick={props.onExportCurrent}>导出当前方案</MenuItem><MenuItem icon={<DocumentArrowUpRegular />} onClick={props.onExportAll}>导出全部方案</MenuItem></MenuList></MenuPopover></Menu><button type="button" onClick={props.onNew} aria-label="新建优化方案" title="新建优化方案"><AddRegular /></button></span></header>
    <div className="study-tree" data-scroll-id="study-tree">
      {props.studies.map((study) => {
        const active = study.id === props.activeStudyId;
        const latest = props.histories.find((entry) => entry.studyId === study.id);
        return <div className={`study-node ${active ? "active" : ""}`} key={study.id}>
          <div className="study-node-row">
            <span className="study-node-icon"><DocumentTextRegular /></span>
            <button type="button" className="study-node-name" aria-current={active ? "page" : undefined} onClick={() => props.onSelectStudy(study.id)} title={study.description || study.name}><span>{study.name}</span><small>{latest ? `${solverResultStatus(latest.result)} · ${formatNumber(latest.result.objectiveScore ?? 0, 2)}` : "尚未运行"}</small></button>
            <Menu><MenuTrigger disableButtonEnhancement><button type="button" className="tree-more" aria-label={`${study.name} 操作`}><MoreHorizontalRegular /></button></MenuTrigger>
              <MenuPopover><MenuList>
                <MenuItem icon={<EditRegular />} onClick={() => props.onRename(study)}>重命名…</MenuItem>
                <MenuItem icon={<CopyRegular />} onClick={() => props.onDuplicate(study.id)}>复制为新方案…</MenuItem>
                <MenuDivider />
                <MenuItem icon={<DeleteRegular />} onClick={() => props.onDelete(study)}>删除方案…</MenuItem>
              </MenuList></MenuPopover>
            </Menu>
          </div>
        </div>;
      })}
    </div>
  </aside>;
}

export function DeveloperExplorer({ page, onOpenPage }: { page: Page; onOpenPage(page: Page): void }): JSX.Element {
  const pages = developerPages.filter((item) => item.id !== "global-settings");
  return <aside className="study-explorer developer-explorer">
    <header><strong>开发资料</strong></header>
    <nav className="developer-tree" aria-label="开发资料">
      {pages.map((item) => <button type="button" key={item.id} className={page === item.id ? "active" : ""} aria-current={page === item.id ? "page" : undefined} onClick={() => onOpenPage(item.id)}><span>{item.label}</span></button>)}
    </nav>
  </aside>;
}

export function RunPanel({ progress, studyName, onCancel, onClose }: { progress: SolverProgressV2 | undefined; studyName: string; onCancel(): void; onClose(): void }): JSX.Element {
  const elapsedMilliseconds = useSolverElapsed(progress);
  return <section className={`ide-bottom-panel${progress ? ' solver-progress-panel' : ''}`} aria-label="运行面板">
    <header><div><button type="button" className="active">运行</button></div><button type="button" className="panel-close" onClick={onClose} aria-label="关闭底部面板"><DismissRegular /></button></header>
    {progress ? <SolverRunDetails key={progress.runId ?? progress.startedAt} progress={progress} elapsedMilliseconds={elapsedMilliseconds} onCancel={onCancel} /> : <div className="run-panel-body">
      <span className="run-state-dot idle" />
      <div><strong>暂无运行任务</strong><small>运行“{studyName}”后，此处显示阶段与耗时</small></div>
      <span className="run-panel-spacer" />
      {elapsedMilliseconds !== undefined && <strong className="run-elapsed">{formatElapsedSeconds(elapsedMilliseconds)}</strong>}
    </div>}
  </section>;
}

export const RunningStatus = memo(function RunningStatus({ progress }: { progress: SolverProgressV2 }) {
  const elapsed = useSolverElapsed(progress);
  return <span className="running-status">{progress.stageLabel ?? '正在计算'}{elapsed === undefined ? '' : ` · ${formatElapsedSeconds(elapsed)}`}</span>;
});

